<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>
          mdi-check
        </v-icon>{{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <validation-observer ref="observerRef">
        <settings-group
          :title="$t('settings.messagecenter.communication_settings')"
          :subtitle="$t('settings.messagecenter.communication_settings_subtitle')"
          :gutter="$vuetify.breakpoint.lgAndUp"
        >
          <v-col
            cols="12"
            md="6"
            class="py-0 my-0"
          >
            <v-select
              v-model="commStyle"
              :items="commStyleItems"
              :label="$t('label.communication_style')"
              item-value="id"
              item-text="name"
            />
          </v-col>
        </settings-group>

        <settings-group
          :title="$t('settings.messagecenter.text_blocks')"
          :subtitle="$t('settings.messagecenter.text_blocks_subtitle')"
          :gutter="$vuetify.breakpoint.lgAndUp"
        >
          <multi-language-group
            v-model="selectedLocale"
            :enabled-locales="enabledLocales"
            :translated-locales="enabledLocales.map((l) => l.locale)"
          >
            <v-col
              cols="12"
              class="d-flex flex-row align-center"
            >
              <rich-text-editor-row
                v-model="header"
                height="20px"
                :label="$t('label.placeholder_header_x', [languageDisplayName])"
                :placeholders="placeholdersHeader"
              >
                <template #menu-items>
                  <v-separator />
                  <v-list-item @click="resetHeader">
                    <v-list-item-title>
                      {{ $t('button.reset_to_default') }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </rich-text-editor-row>
            </v-col>
            <v-col
              cols="12"
              class="d-flex flex-row align-center"
            >
              <rich-text-editor-row
                v-model="footer"
                height="48px"
                :label="$t('label.placeholder_footer_x', [languageDisplayName])"
                :placeholders="placeholdersFooter"
              >
                <template #menu-items>
                  <v-list-item @click="resetSignature">
                    <v-list-item-title>
                      {{ $t('button.reset_to_default') }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </rich-text-editor-row>
            </v-col>
            <v-col
              cols="12"
              class="d-flex flex-row align-center"
            >
              <rich-text-editor-row
                v-model="fixedEndingTimeNote"
                :label="$t('label.placeholder_fixed_ending_x', [languageDisplayName])"
                height="48px"
                :placeholders="placeholdersEndTime"
              >
                <template #menu-items>
                  <v-list-item @click="resetFixedEndingTime">
                    <v-list-item-title>
                      {{ $t('button.reset_to_default') }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </rich-text-editor-row>
            </v-col>
          </multi-language-group>
        </settings-group>
        <settings-group
          :title="$t('settings.messagecenter.advanced_settings')"
          :subtitle="$t('settings.messagecenter.advanced_settings_subtitle')"
          :gutter="$vuetify.breakpoint.lgAndUp"
        >
          <v-col
            cols="12"
            class="py-0 my-0"
          >
            <v-alert
              v-if="!showDetailsInSummary"
              dense
              border="left"
              type="warning"
            >
              {{ $t('message.message_center_details_hidden_from_summary') }}
            </v-alert>
            <v-switch
              v-model="showDetailsInSummary"
              :label="$t('label.show_detais_in_email_summary_block')"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0 my-0"
          >
            <v-switch
              v-model="showRestaurantPhone"
              :label="$t('label.email_show_phone_in_footer')"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0 my-0"
          >
            <v-switch
              v-model="showRestaurantEmail"
              :label="$t('label.show_email_in_email_footer')"
            />
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
/* eslint-disable max-len */
/* eslint-disable vue/max-len */
import {
  computed, getCurrentInstance, onBeforeMount, onBeforeUnmount, ref, watch,
} from 'vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import store from '@/store/store';
import MessageCenterStore from '@/store/message-center-store';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import i18n from '@/plugins/i18n';
import { performAction, performDispatchAction, performSaveAction } from '@/services/vue-utils';
import RichTextEditorRow from '@/components/editors/RichTextEditorRow.vue';
import { useHandlebars } from '@/composables/messagecenter';
import MultiLanguageGroup from '@/components/dialogs/MultiLanguageGroup.vue';
import MessageSettings, { CommunicationFlags, CommunicationStyles } from '@/grpc-api/model/message-settings';
import MessageTranslation, { MessageTranslationKey } from '@/grpc-api/model/message-translation';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import toast from '@/plugins/toast-plugin/toast';
import { strcmp } from '@/services/common';
import { useSortedLanguages } from './settings/helpers/settings-values';

// data
const preferencesLoaded = ref(false);
let oldSettings = new MessageSettings();
let oldTranslations = [] as MessageTranslation[];
const settings = ref(cloneModel(oldSettings));
const translations = ref(oldTranslations.map((t) => cloneModel(t)));

const isChanged = computed(() => !isModelEqualToModel(settings.value, oldSettings) || !isModelEqualToModel(translations.value, oldTranslations));

// Communication style
const commStyle = computed({
  get: () => (settings.value?.communicationStyles?.includes(CommunicationStyles.Informal) ? 0 : 1),
  set: (value: number) => { settings.value.communicationStyles = value === 0 ? [CommunicationStyles.Informal] : []; },
});
const commStyleInformal = computed(() => settings.value?.communicationStyles?.includes(CommunicationStyles.Informal) ?? false);
watch(commStyleInformal, (newVal, oldVal) => {
  if (!preferencesLoaded.value) return;
  if (newVal === oldVal) return;
  /* eslint-disable no-param-reassign */
  translations.value.forEach((t) => {
    if (t.textValue === t.defaultFormalTextValue && newVal) t.textValue = t.defaultInformalTextValue;
    else if (t.textValue === t.defaultInformalTextValue && !newVal) t.textValue = t.defaultFormalTextValue;
  });
}, { immediate: false });

const commStyleItems = [
  { id: 0, name: i18n.t('label.communication_style_informal') },
  { id: 1, name: i18n.t('label.communication_style_formal') },
];

// Languages
const languages = useSortedLanguages();
const selectedLocale = ref('en');
const enabledLocales = computed(() => {
  const localesMap = store.getters.enabledLocalesSet as Set<string>;
  const locales = languages.value.filter((l) => localesMap.has(l.locale));
  console.log('Enabled locales:', locales);
  return locales;
});
const languageDisplayName = computed(() => languages.value.find((l) => l.locale === selectedLocale.value)?.language);

// Advanced settings
const changeFlags = (value: boolean, flag: CommunicationFlags) => {
  let flags = settings.value.communicationFlags || [];
  if (!value) flags = flags.filter((f) => f !== flag);
  else if (!flags.includes(flag)) { flags = [...flags, flag]; flags.sort((a, b) => strcmp(a, b)); }
  settings.value.communicationFlags = flags;
};

const showDetailsInSummary = computed({
  get: () => settings.value?.communicationFlags?.includes(CommunicationFlags.HideDetailsBlock),
  set: (value: boolean) => { changeFlags(value, CommunicationFlags.HideDetailsBlock); },
});

const showRestaurantPhone = computed({
  get: () => !settings.value?.communicationFlags?.includes(CommunicationFlags.HideFooterPhone),
  set: (value: boolean) => { changeFlags(!value, CommunicationFlags.HideFooterPhone); },
});

const showRestaurantEmail = computed({
  get: () => !settings.value?.communicationFlags?.includes(CommunicationFlags.HideFooterEmail),
  set: (value: boolean) => { changeFlags(!value, CommunicationFlags.HideFooterEmail); },
});

// translation
const getTranslation = (key: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  return translation?.textValue ?? 'N/A';
};
const setTranslation = (key: string, value: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  if (translation !== undefined) translation.textValue = value;
};
const resetTranslation = (key: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  if (translation !== undefined) translation.textValue = commStyleInformal.value ? translation?.defaultInformalTextValue : translation?.defaultFormalTextValue;
};

const header = computed({
  get: () => getTranslation(MessageTranslationKey.Heading),
  set: (value: string) => setTranslation(MessageTranslationKey.Heading, value),
});

const footer = computed({
  get: () => getTranslation(MessageTranslationKey.Footer),
  set: (value: string) => setTranslation(MessageTranslationKey.Footer, value),
});

const fixedEndingTimeNote = computed({
  get: () => getTranslation(MessageTranslationKey.FixedEndingTimeNote),
  set: (value: string) => setTranslation(MessageTranslationKey.FixedEndingTimeNote, value),
});

const resetHeader = () => resetTranslation(MessageTranslationKey.Heading);
const resetSignature = () => resetTranslation(MessageTranslationKey.Footer);
const resetFixedEndingTime = () => resetTranslation(MessageTranslationKey.FixedEndingTimeNote);

const placeholders = (textKeys: string[]) => {
  const filtered = useHandlebars.value.filter((o) => textKeys.includes(o.name));
  const mapped = filtered.map((o) => ({ id: o.id, name: i18n.tc(`code.handlebar.${o.name}`), expression: o.expression }));
  console.log('Placeholders:', useHandlebars.value, filtered, mapped);
  return mapped;
};

const placeholdersHeader = computed(() => placeholders(['full_name']));
const placeholdersFooter = computed(() => placeholders(['restaurant_name']));
const placeholdersEndTime = computed(() => placeholders(['end_time']));

// store operation
const reset = async () => {
  oldSettings = store.getters.messageCenterSettings as MessageSettings;
  oldTranslations = store.getters.messageCenterTranslations as MessageTranslation[];
  settings.value = cloneModel(oldSettings);
  translations.value = oldTranslations.map((t) => cloneModel(t));
  selectedLocale.value = settings.value.defaultLocale;
  preferencesLoaded.value = true;
};

const load = async () => {
  if (store.getters.isMessageCenterLoaded) return;

  const ok = await performAction(
    null,
    i18n.tc('error.load_message_center'),
    async () => store.dispatch('loadMessageCenter'),
  );
  if (!ok) return;

  reset();
};

const save = async () => {
  if (!preferencesLoaded.value) return;

  const ok = await performSaveAction(
    null,
    async () => store.dispatch('saveMessageCenterPreferences', { settings: settings.value, translations: translations.value }),
  );
  if (!ok) return;

  reset();
};

onBeforeMount(async () => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'messageCenter', MessageCenterStore, async () => { load(); });
});

onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'messageCenter', async () => { store.dispatch('resetMessageCenter'); });
});

</script>
